import type { PartSpec } from '../types';

export const parts: PartSpec[] = [
  {
    id: 'arduino-uno',
    name: 'Arduino Uno / Nano',
    category: 'Controller',
    image: '/images/arduino_uno.png',
    description: 'The brain of the robot. Reads sensors, runs your strategy, drives the motors.',
    voltage: '5V logic, 7–12V input',
    weight: '25 g',
    price: '~$8–25',
    usedFor: ['sumo', 'line', 'maze', 'battle'],
    simulationBehavior: 'Executes the strategy loop with a configurable reaction delay.',
    wiring: { vcc: 'VIN / USB', gnd: 'GND', signal: 'Digital + Analog pins' },
    beginnerTip: 'Start with the Uno — more tutorials exist for it than any other board.',
    fileNames: ['arduino_uno.glb'],
  },
  {
    id: 'arduino-mega',
    name: 'Arduino Mega / ESP32',
    category: 'Controller',
    image: '/images/arduino_mega.png',
    description: 'More pins and memory for advanced robots with many sensors.',
    voltage: '5V / 3.3V logic',
    weight: '37 g',
    price: '~$15–30',
    usedFor: ['maze', 'battle'],
    simulationBehavior: 'Same as Uno but supports more simultaneous sensors.',
    wiring: { vcc: 'VIN / USB', gnd: 'GND', signal: '54 digital, 16 analog' },
    beginnerTip: 'Pick the ESP32 if you want Bluetooth/Wi-Fi telemetry while testing.',
    optional: true,
    fileNames: ['arduino_mega.glb'],
  },
  {
    id: 'motor-driver',
    name: 'Motor Driver (TB6612 / L298N / BTS7960)',
    category: 'Driver',
    image: '/images/coming_soon.png',
    description: 'Lets the small microcontroller switch the big motor currents, forward and reverse.',
    voltage: '4.5–13.5V (TB6612) / up to 27V (BTS7960)',
    weight: '10–60 g',
    price: '~$3–12',
    usedFor: ['sumo', 'line', 'maze', 'battle'],
    simulationBehavior: 'Limits motor current; cheaper drivers waste voltage and reduce torque.',
    wiring: { vcc: 'Battery +', gnd: 'Common GND', signal: 'PWM + direction pins' },
    beginnerTip: 'The L298N wastes ~2V. For sumo pushing power, prefer TB6612 or BTS7960.',
  },
  {
    id: 'dc-motor',
    name: 'DC Gear Motor',
    category: 'Motion',
    image: '/images/Motor.png',
    description: 'Geared DC motor — the gear ratio trades speed for pushing torque.',
    voltage: '6–12V',
    weight: '90 g',
    price: '~$3–8',
    usedFor: ['sumo', 'line', 'maze', 'battle'],
    simulationBehavior: 'Produces torque; the sim converts it to drive force through the wheel radius.',
    wiring: { vcc: 'Driver OUT1', gnd: 'Driver OUT2', signal: '—' },
    beginnerTip: 'High RPM ≠ winning. Sumo robots want torque; line followers want speed.',
    fileNames: ['Motor.glb'],
  },
  {
    id: 'n20-motor',
    name: 'N20 Micro Motor',
    category: 'Motion',
    image: '/images/N20_motor.png',
    description: 'Tiny geared motor for light, fast robots like line followers.',
    voltage: '6V',
    weight: '10 g',
    price: '~$2–5',
    usedFor: ['line', 'maze'],
    simulationBehavior: 'Low torque, high speed — great acceleration on light chassis.',
    wiring: { vcc: 'Driver OUT1', gnd: 'Driver OUT2', signal: '—' },
    beginnerTip: 'Choose the 6V 500RPM version for a fast but controllable line follower.',
    optional: true,
    fileNames: ['N20_motor.glb'],
  },
  {
    id: 'wheels',
    name: 'Wheels (rubber/silicone)',
    category: 'Motion',
    image: '/images/wheel.png',
    description: 'Grip decides how much of your torque becomes pushing force instead of slipping.',
    weight: '20 g each',
    price: '~$2–10',
    usedFor: ['sumo', 'line', 'maze', 'battle'],
    simulationBehavior: 'Sets the friction coefficient — traction force = grip × weight × 9.81.',
    beginnerTip: 'Soft silicone wheels on a clean arena are the single biggest sumo upgrade.',
    fileNames: ['wheel.glb'],
  },
  {
    id: 'battery',
    name: 'Battery (Li-ion / LiPo / 9V)',
    category: 'Power',
    image: '/images/9V-battery.png',
    description: 'Powers everything. Voltage sag under load slows your motors.',
    voltage: '7.4–11.1V typical',
    weight: '50–180 g',
    price: '~$5–20',
    usedFor: ['sumo', 'line', 'maze', 'battle'],
    simulationBehavior: 'Battery voltage scales max motor speed and torque in the sim.',
    wiring: { vcc: 'Switch → Driver VIN', gnd: 'Common GND', signal: '—' },
    beginnerTip: 'A 9V smoke-alarm battery cannot drive motors well — use Li-ion packs.',
    fileNames: ['9V-battery.glb'],
  },
  {
    id: 'battery-holder',
    name: 'Battery Holder + Power Switch',
    category: 'Power',
    image: '/images/battary_holder.png',
    description: 'Secure battery mounting and a master switch (required by most rule packs).',
    weight: '15 g',
    price: '~$1–3',
    usedFor: ['sumo', 'line', 'maze', 'battle'],
    simulationBehavior: 'Adds weight; position affects the center of mass.',
    beginnerTip: 'Mount the battery low and centered — it lowers the center of mass for free.',
    fileNames: ['battary_holder.glb'],
  },
  {
    id: 'ir-line',
    name: 'IR Line / Edge Sensor',
    category: 'Sensor',
    image: '/images/ir_sensor.png',
    description: 'Detects black/white surface contrast. Edge detection in sumo, line tracking in line follower.',
    voltage: '3.3–5V',
    weight: '3 g',
    price: '~$1–2',
    usedFor: ['sumo', 'line', 'battle'],
    simulationBehavior: 'Reads surface color under the sensor; triggers edge/line events.',
    wiring: { vcc: '5V', gnd: 'GND', signal: 'Digital or Analog pin' },
    beginnerTip: 'Place sensors close to the floor (3–8 mm) for a reliable reading.',
    fileNames: ['ir_sensor.glb', 'ir_shaft.glb'],
  },
  {
    id: 'ir-array',
    name: 'IR Sensor Array (5/8 channel)',
    category: 'Sensor',
    image: '/images/ir_a.png',
    description: 'A row of IR sensors that measures how far the line is from the robot center.',
    voltage: '5V',
    weight: '10 g',
    price: '~$5–12',
    usedFor: ['line'],
    simulationBehavior: 'Produces the position error that feeds your PID controller.',
    wiring: { vcc: '5V', gnd: 'GND', signal: 'A0–A7' },
    beginnerTip: 'More sensors = smoother error signal = less oscillation at speed.',
    fileNames: ['ir_a.glb', 'ir_b.glb'],
  },
  {
    id: 'ultrasonic',
    name: 'Ultrasonic Sensor (HC-SR04)',
    category: 'Sensor',
    image: '/images/ultrasonic.png',
    description: 'Measures distance with sound — finds opponents and maze walls.',
    voltage: '5V',
    weight: '9 g',
    price: '~$1–3',
    usedFor: ['sumo', 'maze', 'battle'],
    simulationBehavior: 'Returns distance within a detection cone; angle and range are configurable.',
    wiring: { vcc: '5V', gnd: 'GND', signal: 'TRIG + ECHO pins' },
    beginnerTip: 'Mount it level. A tilted ultrasonic reads the floor and lies to you.',
  },
  {
    id: 'tof',
    name: 'ToF Laser Sensor (VL53L0X)',
    category: 'Sensor',
    image: '/images/coming_soon.png',
    description: 'Laser time-of-flight ranging: faster and narrower beam than ultrasonic.',
    voltage: '3.3–5V',
    weight: '2 g',
    price: '~$3–6',
    usedFor: ['sumo', 'maze', 'battle'],
    simulationBehavior: 'Narrow accurate cone, faster reading rate than ultrasonic.',
    wiring: { vcc: '3.3V', gnd: 'GND', signal: 'I2C (SDA/SCL)' },
    beginnerTip: 'Great for maze side-walls where ultrasonic echoes bounce badly.',
    optional: true,
  },
  {
    id: 'imu',
    name: 'IMU (MPU6050)',
    category: 'Sensor',
    image: '/images/coming_soon.png',
    description: 'Gyro + accelerometer — measures turning angle and detects impacts.',
    voltage: '3.3–5V',
    weight: '2 g',
    price: '~$2–4',
    usedFor: ['sumo', 'maze', 'battle'],
    simulationBehavior: 'Improves turn accuracy in the maze sim (better turn calibration).',
    wiring: { vcc: '3.3V', gnd: 'GND', signal: 'I2C (SDA/SCL)' },
    beginnerTip: 'Use it to make exact 90° maze turns instead of timed turns.',
    optional: true,
  },
  {
    id: 'chassis',
    name: 'Chassis',
    category: 'Structure',
    image: '/images/chassis.png',
    description: 'The body. Size and shape decide stability, and must fit the rule pack limits.',
    weight: '80–300 g',
    price: '~$5–20',
    usedFor: ['sumo', 'line', 'maze', 'battle'],
    simulationBehavior: 'Sets footprint and base weight; wider = more stable, heavier = slower.',
    beginnerTip: 'Check maxWidthCm / maxLengthCm in the rules BEFORE you build.',
    fileNames: ['chassis.glb', 'chassis_connector.glb'],
  },
  {
    id: 'start-button',
    name: 'Start Button',
    category: 'Other',
    image: '/images/button.png',
    description: 'Starts the robot after the countdown — required for autonomous matches.',
    voltage: '5V',
    weight: '1 g',
    price: '<$1',
    usedFor: ['sumo', 'line', 'maze', 'battle'],
    simulationBehavior: 'Begins the strategy loop after the rule pack start delay.',
    wiring: { vcc: '5V', gnd: 'GND', signal: 'Digital pin (pull-down)' },
    beginnerTip: 'Add a 5-second delay after pressing — most sumo rules require it.',
    fileNames: ['button.glb'],
  },
  {
    id: 'bluetooth',
    name: 'Bluetooth Module (HC-05)',
    category: 'Other',
    image: '/images/bluetooth_module.png',
    description: 'Wireless telemetry for tuning PID values without re-uploading code. Testing only!',
    voltage: '3.3–5V',
    weight: '4 g',
    price: '~$3–5',
    usedFor: ['line', 'battle'],
    simulationBehavior: 'Not simulated — used for real-robot calibration later.',
    wiring: { vcc: '5V', gnd: 'GND', signal: 'TX/RX' },
    beginnerTip: 'Most competitions ban radio during matches — use it for tuning only.',
    optional: true,
    fileNames: ['bluetooth_module.glb'],
  },
  {
    id: 'display',
    name: 'OLED / LCD Display',
    category: 'Other',
    image: '/images/lcd_display.png',
    description: 'Shows sensor values and the selected strategy before a match.',
    voltage: '3.3–5V',
    weight: '8 g',
    price: '~$3–6',
    usedFor: ['line', 'maze'],
    simulationBehavior: 'Not simulated — quality-of-life part for real builds.',
    wiring: { vcc: '5V', gnd: 'GND', signal: 'I2C (SDA/SCL)' },
    beginnerTip: 'Display the calibration values on screen — debugging without a laptop.',
    optional: true,
    fileNames: ['lcd_display.glb'],
  },
  {
    id: 'breadboard',
    name: 'Breadboard',
    category: 'Structure',
    image: '/images/coming_soon.png',
    description: 'Solderless prototyping board for wiring sensors, drivers, and the controller together.',
    weight: '30 g',
    price: '~$2–5',
    usedFor: ['sumo', 'line', 'maze', 'battle'],
    simulationBehavior: 'Not simulated — used to wire the real circuit in the workbench.',
    beginnerTip: 'Keep power and ground rails tidy — most "dead robot" bugs are wiring mistakes.',
    fileNames: ['breadboard.glb'],
  },
];

export function partsFor(id: PartSpec['usedFor'][number]): { required: PartSpec[]; optional: PartSpec[] } {
  const all = parts.filter((p) => p.usedFor.includes(id));
  return {
    required: all.filter((p) => !p.optional),
    optional: all.filter((p) => p.optional),
  };
}
